import { Person, Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import { PaperProps } from '@mui/material/Paper/Paper';
import { SvgIconProps } from '@mui/material/SvgIcon/SvgIcon';
import { useTheme } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';

export enum WorkspaceRestrictionState {
  Restricted = 'restricted',
  Unrestricted = 'unrestricted',
  Personal = 'personal',
}

type PropsType = PaperProps & {
  restrictionType: WorkspaceRestrictionState;
};

type RestrictionStatementProps = {
  label: string;
  description: string;
  icon: (iconProps: SvgIconProps) => React.ReactNode;
};

export const WorkspaceRestrictionInfo = (props: PropsType) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { restrictionType, children, sx, ...paperProps } = props;

  const statements: {
    [key in WorkspaceRestrictionState]: RestrictionStatementProps;
  } = {
    restricted: {
      label: t('workspaceIsRestricted'),
      description: t('workspaceIsRestrictedDescription'),
      icon: (iconProps: SvgIconProps) => <VisibilityOff {...iconProps} />,
    },
    unrestricted: {
      label: t('workspaceIsUnrestricted'),
      description: t('workspaceIsUnrestrictedDescription'),
      icon: (iconProps: SvgIconProps) => <Visibility {...iconProps} />,
    },
    personal: {
      label: t('workspaceIsPersonal'),
      description: t('workspaceIsPersonalDescription'),
      icon: (iconProps: SvgIconProps) => <Person {...iconProps} />,
    },
  };

  const iconProps: SvgIconProps = {
    fontSize: 'inherit',
    sx: { verticalAlign: 'middle', marginLeft: '4px' },
  };

  const statementProps: RestrictionStatementProps = statements[restrictionType];

  return (
    <Paper
      sx={{
        padding: theme.spacing(1, 2),
        ...sx,
      }}
      {...paperProps}
    >
      <Box>
        <Typography variant={'body1'}>
          {statementProps.label} {statementProps.icon(iconProps)}
        </Typography>
        <Typography variant={'body2'}>{statementProps.description}</Typography>
      </Box>

      {children}
    </Paper>
  );
};
